/**
 * PatientInfoDisplay — Read-only display of patient information on report editor page.
 * Shows patient demographics and study details when available.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LockIcon from '@mui/icons-material/Lock';
import patientService, { PatientResponse } from '../services/patientService';

interface PatientInfoDisplayProps {
  sessionId: string;
}

const PatientInfoDisplay: React.FC<PatientInfoDisplayProps> = ({ sessionId }) => {
  const [patient, setPatient] = useState<PatientResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const loadPatient = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await patientService.getSessionPatient(sessionId);
        if (active) {
          setPatient(data);
        }
      } catch (err: any) {
        if (active) {
          if (err?.response?.status === 404) {
            // No patient linked to this session - this is OK
            setPatient(null);
          } else {
            setError('Failed to load patient information');
          }
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadPatient();

    return () => {
      active = false;
    };
  }, [sessionId]);

  if (loading) {
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading patient information...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="outlined" sx={{ mb: 2, borderColor: 'error.main' }}>
        <CardContent>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!patient) {
    return null; // No patient linked - don't show anything
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2, bgcolor: 'grey.50' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={600}>
              Patient Information
            </Typography>
          </Box>
          <Chip
            icon={<LockIcon sx={{ fontSize: 12 }} aria-hidden="true" />}
            label="PHI Encrypted"
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontSize: '0.68rem', height: 22 }}
          />
        </Box>

        <Grid container spacing={2}>
          {/* Name */}
          {(patient.first_name || patient.last_name) && (
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Patient Name
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {[patient.first_name, patient.last_name].filter(Boolean).join(' ') || 'N/A'}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* MRN */}
          {patient.mrn && (
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  <LocalHospitalIcon sx={{ fontSize: 11, verticalAlign: 'middle', mr: 0.5 }} />
                  MRN
                </Typography>
                <Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace' }}>
                  {patient.mrn}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* DOB */}
          {patient.dob && (
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  <CalendarTodayIcon sx={{ fontSize: 11, verticalAlign: 'middle', mr: 0.5 }} />
                  Date of Birth
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {formatDate(patient.dob)}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Gender */}
          {patient.gender && (
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Gender
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {patient.gender === 'M' ? 'Male' : patient.gender === 'F' ? 'Female' : patient.gender}
                </Typography>
              </Box>
            </Grid>
          )}

        </Grid>

      </CardContent>
    </Card>
  );
};

export default PatientInfoDisplay;
